import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ClipboardCheck, History, ListChecks, Mail, Plus, Save } from 'lucide-react';
import CustomDropdown from './CustomDropdown';
import CertificationPathways from './CertificationPathways';
import { useApiClient } from '../auth/useApiClient';
import CreatableDropdown from './pathways/CreatableDropdown';
import InfoTooltip from './pathways/InfoTooltip';
import {
  PATHWAY_DRAFT_KEY,
  defaultProfile,
  labelValue,
  objectivePresets,
  objectiveSuggestionPriority,
  readPathwayDraft,
  readinessFields,
  roleSuggestions,
  sectorSuggestions
} from './pathways/pathwayConfig';
import { buildDraftEmail, buildDraftEmailHtml, plainTextEmailToHtml } from './pathways/pathwayDraftEmail';
import {
  normalizeCourseTitle,
  rankSuggestions
} from './pathways/pathwayHelpers';
import PathwayHistoryDrawer from './pathways/PathwayHistoryDrawer';
import PathwayEmailPanel from './pathways/PathwayEmailPanel';
import PathwayRecommendationPanel from './pathways/PathwayRecommendationPanel';
import PathwaySaveDialog from './pathways/PathwaySaveDialog';

function buildInitialPathwayChatMessage(pathways = []) {
  if (!Array.isArray(pathways) || pathways.length === 0) {
    return 'I can help compare the recommendation once a pathway bundle is generated.';
  }

  const labels = pathways.slice(0, 5).map((pathway) => pathway.label).join(', ');
  return `I found ${pathways.length} pathway options: ${labels}. Ask me to compare them, make the route more leadership/strategy/finance/digital oriented, or explain which one is safest to position.`;
}

function getPreferredPathwayId(pathways = [], preferredPathwayId = '') {
  if (!Array.isArray(pathways) || pathways.length === 0) return null;
  const pathwayIds = new Set(pathways.map((pathway) => pathway.id).filter(Boolean));
  if (preferredPathwayId && pathwayIds.has(preferredPathwayId)) {
    return preferredPathwayId;
  }
  return pathways.find((pathway) => pathway.isPreferred)?.id || null;
}

const PATHWAY_CHAT_PREFS_KEY = 'execEye.pathwayChatPreferences';

function readPathwayChatPreferences() {
  try {
    return JSON.parse(window.localStorage.getItem(PATHWAY_CHAT_PREFS_KEY) || '{}');
  } catch (error) {
    return {};
  }
}

async function readServerEventStream(response, handlers = {}) {
  if (!response.body) {
    throw new Error('Streaming response is not available in this browser.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const processEvent = (rawEvent) => {
    const lines = rawEvent.split(/\r?\n/);
    const eventName = lines.find((line) => line.startsWith('event:'))?.slice(6).trim() || 'message';
    const dataText = lines
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).trimStart())
      .join('\n');

    if (!dataText) return;

    let data;
    try {
      data = JSON.parse(dataText);
    } catch (error) {
      data = { message: dataText };
    }

    if (eventName === 'status') handlers.onStatus?.(data);
    if (eventName === 'result') handlers.onResult?.(data);
    if (eventName === 'error') handlers.onError?.(data);
  };

  while (true) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
    const events = buffer.split(/\n\n/);
    buffer = events.pop() || '';
    events.forEach(processEvent);
    if (done) break;
  }

  if (buffer.trim()) {
    processEvent(buffer);
  }
}

function buildLocalPathwayChatResponse(pathways = [], message = '') {
  const normalized = String(message || '').toLowerCase();
  const themeTerms = [
    { theme: 'digital', words: ['digital', 'transformation', 'technology', 'ai', 'analytics'] },
    { theme: 'finance', words: ['finance', 'financial', 'commercial', 'board'] },
    { theme: 'strategy', words: ['strategy', 'strategic', 'growth', 'execution'] },
    { theme: 'leadership', words: ['leadership', 'people', 'change', 'stakeholder'] },
    { theme: 'balanced', words: ['balanced', 'overall', 'safe', 'broad'] }
  ];
  const requestedTheme = themeTerms.find(({ words }) => words.some((word) => normalized.includes(word)))?.theme;
  const matchedPathway = requestedTheme
    ? pathways.find((pathway) => String(pathway.theme || '').toLowerCase() === requestedTheme)
    : null;

  if (matchedPathway) {
    return {
      response: `${matchedPathway.label} is the closest match. ${matchedPathway.summary || ''}${matchedPathway.tradeoffs ? ` Main trade-off: ${matchedPathway.tradeoffs}` : ''}`,
      suggestedPathwayId: matchedPathway.id
    };
  }

  return {
    response: `The main choice is emphasis: ${pathways.map((pathway) => pathway.label).join(', ')}. Ask for a stronger leadership, strategy, finance, digital, or balanced route and I will point you to the closest bundle.`,
    suggestedPathwayId: null
  };
}

function normalizeReviewIssues(items = []) {
  return (Array.isArray(items) ? items : [])
    .map((item) => {
      if (typeof item === 'string') {
        return { source: 'workflow', message: item };
      }
      return {
        source: item?.source || 'workflow',
        severity: item?.severity || 'warning',
        location: item?.location || '',
        programmeCode: item?.programmeCode || '',
        message: item?.message || item?.fixInstruction || '',
        fixInstruction: item?.fixInstruction || ''
      };
    })
    .filter((item) => item.message || item.fixInstruction);
}

function normalizeAdviserNotesFromResult(data = {}) {
  if (Array.isArray(data.adviserNotes)) {
    return data.adviserNotes
      .map((item) => ({
        type: item?.type || 'note',
        message: item?.message || ''
      }))
      .filter((item) => item.message);
  }

  return [
    ...(Array.isArray(data.assumptions) ? data.assumptions.map((message) => ({ type: 'assumption', message })) : []),
    ...(Array.isArray(data.warnings) ? data.warnings.map((message) => ({ type: 'note', message })) : [])
  ].filter((item) => item.message);
}

function reviewSourceLabel(source = '') {
  const labels = {
    'local-code-check': 'Catalogue check',
    'recommender-output': 'Recommender output',
    'verifier-output': 'Verifier output',
    verifier: 'Verifier',
    workflow: 'Workflow'
  };
  return labels[source] || source || 'Workflow';
}

function reviewIssueText(issue = {}) {
  const code = issue.programmeCode ? `${issue.programmeCode}: ` : '';
  const location = issue.location && issue.location !== issue.source ? `${issue.location} - ` : '';
  return `${reviewSourceLabel(issue.source)}: ${location}${code}${issue.message || issue.fixInstruction}`;
}

function adviserNoteText(note = {}) {
  const label = note.type === 'assumption' ? 'Assumption' : 'Note';
  return `${label}: ${note.message}`;
}

function PathwayReviewSummary({
  status,
  tone,
  reviewIssues = [],
  adviserNotes = [],
  reviewTrail = [],
  isLoading = false
}) {
  if (!status && reviewIssues.length === 0 && adviserNotes.length === 0 && reviewTrail.length === 0) {
    return null;
  }

  return (
    <div className={`pathway-generation-status ${tone}`}>
      {status && (
        <div className="pathway-status-line">
          {isLoading && <span className="pathway-status-spinner" aria-hidden="true" />}
          <p>{status}</p>
        </div>
      )}
      {reviewIssues.length > 0 && (
        <div className="pathway-review-block pathway-review-block--issues">
          <div className="section-label">Review Issues</div>
          <ul>
            {reviewIssues.slice(0, 6).map((issue, index) => (
              <li key={`review-issue-${index}`}>{reviewIssueText(issue)}</li>
            ))}
          </ul>
        </div>
      )}
      {adviserNotes.length > 0 && (
        <div className="pathway-review-block pathway-review-block--notes">
          <div className="section-label">Adviser Notes</div>
          <ul>
            {adviserNotes.slice(0, 6).map((note, index) => (
              <li key={`adviser-note-${index}`}>{adviserNoteText(note)}</li>
            ))}
          </ul>
        </div>
      )}
      {reviewTrail.length > 0 && (
        <details className="pathway-review-trail">
          <summary>Review trail ({reviewTrail.length} attempt{reviewTrail.length === 1 ? '' : 's'})</summary>
          {reviewTrail.map((attempt) => {
            const localIssues = normalizeReviewIssues(attempt.localValidationIssues);
            const verifierIssues = normalizeReviewIssues(attempt.verifierIssues);
            const attemptNotes = [
              ...(Array.isArray(attempt.recommenderAssumptions) ? attempt.recommenderAssumptions.map((message) => ({ type: 'assumption', message })) : []),
              ...(Array.isArray(attempt.recommenderNotes) ? attempt.recommenderNotes.map((message) => ({ type: 'note', message })) : [])
            ].filter((item) => item.message);
            const pathwayCodes = Array.isArray(attempt.outputSummary?.pathways)
              ? attempt.outputSummary.pathways.map((pathway) => `${pathway.label || pathway.id}: ${(pathway.programmeCodes || []).join(', ')}`).filter(Boolean)
              : [];
            const verifierStatus = attempt.verifierRan
              ? (attempt.verifierPassed ? 'Verifier passed' : 'Verifier flagged issues')
              : (localIssues.length > 0 ? 'Catalogue check flagged issues' : 'Verifier not run');

            return (
              <div key={`review-trail-${attempt.attempt}`} className="pathway-review-trail-attempt">
                <div className="pathway-review-trail-head">
                  <strong>Attempt {attempt.attempt}</strong>
                  <span>{verifierStatus} - {localIssues.length + verifierIssues.length} issue{localIssues.length + verifierIssues.length === 1 ? '' : 's'}</span>
                </div>
                {pathwayCodes.length > 0 && (
                  <div className="pathway-review-trail-lines">
                    {pathwayCodes.slice(0, 4).map((line, index) => <span key={`trail-code-${index}`}>{line}</span>)}
                  </div>
                )}
                {attemptNotes.length > 0 && (
                  <ul>
                    {attemptNotes.slice(0, 3).map((note, index) => <li key={`trail-note-${index}`}>{adviserNoteText(note)}</li>)}
                  </ul>
                )}
                {(localIssues.length > 0 || verifierIssues.length > 0) && (
                  <ul>
                    {[...localIssues, ...verifierIssues].slice(0, 6).map((issue, index) => (
                      <li key={`trail-issue-${index}`}>{reviewIssueText(issue)}</li>
                    ))}
                  </ul>
                )}
                {attempt.verifierParseError && (
                  <div className="pathway-review-diagnostic">
                    Verifier parse detail: {attempt.verifierParseError}
                  </div>
                )}
                {attempt.verifierRawTextSnippet && (
                  <pre className="pathway-review-raw-output">{attempt.verifierRawTextSnippet}</pre>
                )}
                {attempt.repairInstruction && (
                  <div className="pathway-review-repair">Repair requested after this attempt.</div>
                )}
              </div>
            );
          })}
        </details>
      )}
    </div>
  );
}

function PathwayPanel({
  title,
  icon: Icon,
  meta,
  open,
  onToggle,
  actions,
  children,
  className = ''
}) {
  return (
    <section className={`exec-panel ${className} ${open ? '' : 'exec-panel--closed'}`}>
      <header
        className="exec-panel__head"
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggle();
          }
        }}
      >
        <div className="exec-panel__heading">
          <ChevronDown size={16} className={`exec-panel__chevron ${open ? 'open' : ''}`} />
          {Icon && <Icon size={17} />}
          <h2>{title}</h2>
          {meta && <span className="exec-panel__meta">{meta}</span>}
        </div>
        {actions && (
          <div className="exec-panel__actions" onClick={(event) => event.stopPropagation()}>
            {actions}
          </div>
        )}
      </header>
      <div className="exec-panel__body" hidden={!open}>{children}</div>
    </section>
  );
}

const PathwayBuilder = () => {
  const { request } = useApiClient();
  const initialPathwayDraftRef = useRef(readPathwayDraft());
  const initialPathwayChatPrefsRef = useRef(readPathwayChatPreferences());
  const initialPathwayChatPrefs = initialPathwayChatPrefsRef.current;
  const [profile, setProfile] = useState({ ...defaultProfile, ...(initialPathwayDraftRef.current.profile || {}) });
  const [profileEntryMode, setProfileEntryMode] = useState(initialPathwayDraftRef.current.profileEntryMode || 'structured');
  const [freeTextProfile, setFreeTextProfile] = useState(initialPathwayDraftRef.current.freeTextProfile || '');
  const [courses, setCourses] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [pathways, setPathways] = useState([]);
  const [alreadyTakenCourses, setAlreadyTakenCourses] = useState([]);
  const [pathwayOpenAIResponseId, setPathwayOpenAIResponseId] = useState(initialPathwayDraftRef.current.pathwayOpenAIResponseId || '');
  const [generationStatus, setGenerationStatus] = useState('');
  const [generationStatusTone, setGenerationStatusTone] = useState('neutral');
  const [recommendationReviewIssues, setRecommendationReviewIssues] = useState([]);
  const [recommendationAdviserNotes, setRecommendationAdviserNotes] = useState([]);
  const [recommendationReviewTrail, setRecommendationReviewTrail] = useState([]);
  const [selectedPathwayId, setSelectedPathwayId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showReadinessDetails, setShowReadinessDetails] = useState(false);
  const [isProfilePanelCollapsed, setIsProfilePanelCollapsed] = useState(false);
  const [isRecommendationsPanelCollapsed, setIsRecommendationsPanelCollapsed] = useState(false);
  const [isEmailPanelCollapsed, setIsEmailPanelCollapsed] = useState(false);
  const [copyStatus, setCopyStatus] = useState('');
  const [takenCourseQuery, setTakenCourseQuery] = useState('');
  const [savedPathways, setSavedPathways] = useState([]);
  const [currentSavedPathwayId, setCurrentSavedPathwayId] = useState(null);
  const [editingPathwayId, setEditingPathwayId] = useState(null);
  const [editingPathwayTitle, setEditingPathwayTitle] = useState('');
  const [isSavedPathwaysOpen, setIsSavedPathwaysOpen] = useState(false);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [savePathwayTitle, setSavePathwayTitle] = useState('');
  const [savePathwayError, setSavePathwayError] = useState('');
  const [isSavingPathway, setIsSavingPathway] = useState(false);
  const [pathwayStage, setPathwayStage] = useState('profile');
  const [highlightedPathwayId, setHighlightedPathwayId] = useState(null);
  const [selectedPathwayIds, setSelectedPathwayIds] = useState([]);
  const [pathwayChatMessages, setPathwayChatMessages] = useState([]);
  const [pathwayChatInput, setPathwayChatInput] = useState('');
  const [isPathwayChatLoading, setIsPathwayChatLoading] = useState(false);
  const [pathwayChatModels, setPathwayChatModels] = useState([]);
  const [pathwayChatModel, setPathwayChatModel] = useState(initialPathwayChatPrefs.selectedModel || '');
  const [pathwayChatReasoningEffort, setPathwayChatReasoningEffort] = useState(initialPathwayChatPrefs.reasoningEffort || '');
  const [emailChatModel, setEmailChatModel] = useState(initialPathwayChatPrefs.emailModel || initialPathwayChatPrefs.selectedModel || '');
  const [emailChatReasoningEffort, setEmailChatReasoningEffort] = useState(initialPathwayChatPrefs.emailReasoningEffort || initialPathwayChatPrefs.reasoningEffort || '');
  const [emailChatMessages, setEmailChatMessages] = useState([]);
  const [emailChatInput, setEmailChatInput] = useState('');
  const [isEmailChatLoading, setIsEmailChatLoading] = useState(false);
  const [isDraftGenerating, setIsDraftGenerating] = useState(false);
  const [draftEmailText, setDraftEmailText] = useState('');
  const [draftEmailHtmlContent, setDraftEmailHtmlContent] = useState('');
  const [isPathwayDirty, setIsPathwayDirty] = useState(false);
  const copyStatusTimeoutRef = useRef(null);
  const draftEditorRef = useRef(null);
  const previousGeneratedDraftRef = useRef('');
  const preserveLoadedDraftRef = useRef(false);

  useEffect(() => {
    request('/api/courses')
      .then((response) => response.json())
      .then((data) => {
        setCourses(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        setCourses([]);
      });
  }, [request]);

  useEffect(() => {
    request('/api/pathways/saved')
      .then((response) => (response.ok ? response.json() : { pathways: [] }))
      .then((data) => setSavedPathways(Array.isArray(data.pathways) ? data.pathways : []))
      .catch(() => setSavedPathways([]));
  }, [request]);

  useEffect(() => {
    request('/api/openai/models')
      .then((response) => (response.ok ? response.json() : { models: [] }))
      .then((data) => {
        const availableModels = Array.isArray(data.models) ? data.models : [];
        setPathwayChatModels(availableModels);
        setPathwayChatModel((currentModel) => {
          const persistedModel = currentModel || initialPathwayChatPrefs.selectedModel || '';
          const matchedModel = availableModels.find((model) => model.id === persistedModel);
          const nextModel = matchedModel || availableModels[0];
          if (!nextModel) return '';

          setPathwayChatReasoningEffort((currentEffort) => {
            if (!nextModel.supportsReasoning) return '';
            if (currentEffort && nextModel.reasoningEfforts?.includes(currentEffort)) return currentEffort;
            if (initialPathwayChatPrefs.reasoningEffort && nextModel.reasoningEfforts?.includes(initialPathwayChatPrefs.reasoningEffort)) {
              return initialPathwayChatPrefs.reasoningEffort;
            }
            return nextModel.reasoningEfforts?.[1] || nextModel.reasoningEfforts?.[0] || '';
          });

          return nextModel.id;
        });
        setEmailChatModel((currentModel) => {
          const persistedModel = currentModel || initialPathwayChatPrefs.emailModel || initialPathwayChatPrefs.selectedModel || '';
          const matchedModel = availableModels.find((model) => model.id === persistedModel);
          const nextModel = matchedModel || availableModels[0];
          if (!nextModel) return '';

          setEmailChatReasoningEffort((currentEffort) => {
            if (!nextModel.supportsReasoning) return '';
            if (currentEffort && nextModel.reasoningEfforts?.includes(currentEffort)) return currentEffort;
            if (initialPathwayChatPrefs.emailReasoningEffort && nextModel.reasoningEfforts?.includes(initialPathwayChatPrefs.emailReasoningEffort)) {
              return initialPathwayChatPrefs.emailReasoningEffort;
            }
            if (initialPathwayChatPrefs.reasoningEffort && nextModel.reasoningEfforts?.includes(initialPathwayChatPrefs.reasoningEffort)) {
              return initialPathwayChatPrefs.reasoningEffort;
            }
            return nextModel.reasoningEfforts?.[1] || nextModel.reasoningEfforts?.[0] || '';
          });

          return nextModel.id;
        });
      })
      .catch(() => setPathwayChatModels([]));
  }, [initialPathwayChatPrefs.emailModel, initialPathwayChatPrefs.emailReasoningEffort, initialPathwayChatPrefs.reasoningEffort, initialPathwayChatPrefs.selectedModel, request]);

  useEffect(() => {
    window.localStorage.setItem(PATHWAY_DRAFT_KEY, JSON.stringify({
      profile,
      profileEntryMode,
      freeTextProfile,
      pathwayOpenAIResponseId
    }));
  }, [freeTextProfile, pathwayOpenAIResponseId, profile, profileEntryMode]);

  useEffect(() => {
    window.localStorage.setItem(PATHWAY_CHAT_PREFS_KEY, JSON.stringify({
      selectedModel: pathwayChatModel,
      reasoningEffort: pathwayChatReasoningEffort,
      emailModel: emailChatModel,
      emailReasoningEffort: emailChatReasoningEffort
    }));
  }, [emailChatModel, emailChatReasoningEffort, pathwayChatModel, pathwayChatReasoningEffort]);

  useEffect(() => {
    const selected = pathwayChatModels.find((model) => model.id === pathwayChatModel);
    if (!selected?.supportsReasoning) {
      setPathwayChatReasoningEffort('');
    } else if (!pathwayChatReasoningEffort) {
      setPathwayChatReasoningEffort(selected.reasoningEfforts?.[1] || selected.reasoningEfforts?.[0] || '');
    }
  }, [pathwayChatModel, pathwayChatModels, pathwayChatReasoningEffort]);

  useEffect(() => {
    const selected = pathwayChatModels.find((model) => model.id === emailChatModel);
    if (!selected?.supportsReasoning) {
      setEmailChatReasoningEffort('');
    } else if (!emailChatReasoningEffort) {
      setEmailChatReasoningEffort(selected.reasoningEfforts?.[1] || selected.reasoningEfforts?.[0] || '');
    }
  }, [emailChatModel, emailChatReasoningEffort, pathwayChatModels]);

  useEffect(() => {
    return () => {
      if (copyStatusTimeoutRef.current) {
        clearTimeout(copyStatusTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    window.__execEyeHasUnsavedPathway = isPathwayDirty;

    const handleBeforeUnload = (event) => {
      if (!isPathwayDirty) return undefined;
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.__execEyeHasUnsavedPathway = false;
    };
  }, [isPathwayDirty]);

  const handleInputChange = (field, value) => {
    setIsPathwayDirty(true);
    setProfile((prev) => {
      const nextProfile = { ...prev, [field]: value };

      if (field === 'managementExperience') {
        const seniorityByExperience = {
          '0-3': 'Entry',
          '4-7': 'Mid',
          '8-12': 'Senior',
          '12+': 'Executive'
        };
        nextProfile.seniority = seniorityByExperience[value] || nextProfile.seniority;
      }

      if (field === 'priorExecutiveEducation' && value === 'none') {
        nextProfile.alreadyTakenCourses = [];
        setTakenCourseQuery('');
      }

      if (field === 'certificationPath') {
        const nextCertification = (value || '').toUpperCase();
        nextProfile.alreadyTakenCourses = (nextProfile.alreadyTakenCourses || []).filter((courseTitle) => {
          const matchedCourse = courses.find((course) => normalizeCourseTitle(course.title) === normalizeCourseTitle(courseTitle));
          return matchedCourse && Array.isArray(matchedCourse.pathways) && matchedCourse.pathways.includes(nextCertification);
        });
        setTakenCourseQuery('');
      }

      // When objective changes, prefill role/sector only if the user has not set them yet.
      if (field === 'primaryObjective' && value) {
        const priority = objectiveSuggestionPriority[value] || { roles: [], sectors: [] };
        const rankedRoles = rankSuggestions(roleSuggestions, priority.roles);
        const rankedSectors = rankSuggestions(sectorSuggestions, priority.sectors);

        const hasCareerLevel = Array.isArray(nextProfile.careerLevels)
          ? nextProfile.careerLevels.length > 0
          : Boolean((nextProfile.role || '').trim());

        if (!hasCareerLevel && rankedRoles[0]) {
          nextProfile.careerLevels = [rankedRoles[0]];
          nextProfile.role = rankedRoles[0];
        }

        if (!nextProfile.sector && rankedSectors[0]) {
          nextProfile.sector = rankedSectors[0];
        }
      }

      return nextProfile;
    });
  };

  const handleProfileEntryModeChange = (mode) => {
    setIsPathwayDirty(true);
    setProfileEntryMode(mode);
  };

  const handleFreeTextProfileChange = (value) => {
    setIsPathwayDirty(true);
    setFreeTextProfile(value);
  };

  const selectedCareerLevels = Array.isArray(profile.careerLevels)
    ? profile.careerLevels
    : (profile.role || '').split(',').map((value) => value.trim()).filter(Boolean);

  const handleCareerLevelChange = (careerLevel) => {
    const nextCareerLevel = String(careerLevel || '').trim();
    setIsPathwayDirty(true);
    setProfile((prev) => ({
      ...prev,
      careerLevels: nextCareerLevel ? [nextCareerLevel] : [],
      role: nextCareerLevel
    }));
  };

  const applyObjectivePreset = () => {
    const preset = objectivePresets[profile.primaryObjective];
    if (!preset) return;

    setIsPathwayDirty(true);
    setProfile((prev) => {
      const nextProfile = { ...prev };
      Object.entries(preset).forEach(([field, value]) => {
        if (!nextProfile[field]) {
          nextProfile[field] = value;
        }
      });
      return nextProfile;
    });
  };

  const handleRecommend = async () => {
    setLoading(true);
    setIsPathwayDirty(true);
    setGenerationStatus('Preparing the participant profile.');
    setGenerationStatusTone('neutral');
    setRecommendationReviewIssues([]);
    setRecommendationAdviserNotes([]);
    setRecommendationReviewTrail([]);
    preserveLoadedDraftRef.current = false;
    setDraftEmailText('');
    setDraftEmailHtmlContent('');

    const longRunningTimer = window.setTimeout(() => {
      setGenerationStatus('This is taking longer than usual, but the agents are still working through the recommendation and checks.');
      setGenerationStatusTone('neutral');
    }, 120000);

    try {
      const normalizedCertificationPath = (profile.certificationPath || '').toUpperCase();
      const pathwayType = normalizedCertificationPath === 'CIF'
        ? 'cif'
        : normalizedCertificationPath === 'CIM'
          ? 'cim'
          : '';

      const response = await request('/api/recommend/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profileMode: profileEntryMode,
          freeTextProfile,
          profile: {
            ...profile,
            role: selectedCareerLevels.join(', ') || profile.role,
            careerLevels: selectedCareerLevels,
            pathwayType
          }
        })
      });
      if (!response.ok) {
        throw new Error('Recommendation request failed');
      }

      let data = null;
      await readServerEventStream(response, {
        onStatus: (event) => {
          setGenerationStatus(event.message || 'The agents are working on the recommendation.');
          setGenerationStatusTone(event.phase === 'needs-review' || event.phase === 'source-warning' ? 'warning' : 'neutral');
        },
        onResult: (eventData) => {
          data = eventData;
        },
        onError: (eventData) => {
          throw new Error(eventData.message || 'Recommendation request failed');
        }
      });

      if (!data) {
        throw new Error('No recommendation result was returned.');
      }

      const nextRecommendations = data.recommendations || [];
      const nextPathways = data.pathways || [];
      const nextReviewIssues = normalizeReviewIssues(data.reviewIssues || (data.verification?.needsReview ? data.verification.issues : []));
      const nextAdviserNotes = normalizeAdviserNotesFromResult(data);
      const nextReviewTrail = Array.isArray(data.reviewTrail) ? data.reviewTrail : [];
      setRecommendations(nextRecommendations);
      setPathways(nextPathways);
      setAlreadyTakenCourses(data.alreadyTakenCourses || []);
      setPathwayOpenAIResponseId(data.openaiResponseId || '');
      setRecommendationReviewIssues(nextReviewIssues);
      setRecommendationAdviserNotes(nextAdviserNotes);
      setRecommendationReviewTrail(nextReviewTrail);
      if (nextPathways.length > 0) {
        const preferredPathwayId = getPreferredPathwayId(nextPathways, data.preferredPathwayId);
        setHighlightedPathwayId(preferredPathwayId || null);
        setSelectedPathwayIds(preferredPathwayId ? [preferredPathwayId] : []);
        setSelectedPathwayId(preferredPathwayId || null);
        setPathwayStage('recommendations');
        setIsRecommendationsPanelCollapsed(false);
        setPathwayChatInput('');
        setPathwayChatMessages([
          { role: 'assistant', content: buildInitialPathwayChatMessage(nextPathways) }
        ]);
        setEmailChatInput('');
        setEmailChatMessages([]);
      } else {
        setSelectedPathwayId(null);
        setHighlightedPathwayId(null);
        setSelectedPathwayIds([]);
        setPathwayStage(nextRecommendations.length > 0 ? 'email' : 'profile');
        setPathwayChatInput('');
        setPathwayChatMessages([]);
        setEmailChatInput('');
        setEmailChatMessages([]);
        if (nextReviewIssues.length > 0 || nextAdviserNotes.length > 0 || data.verification?.needsReview) {
          setPathwayStage('recommendations');
          setIsRecommendationsPanelCollapsed(false);
          setPathwayChatMessages([
            { role: 'assistant', content: 'I could not display pathway cards because the recommendation output did not pass the app checks. The review issues above show what went wrong.' }
          ]);
        }
      }
      const hasReviewIssues = data.verification?.needsReview || nextReviewIssues.length > 0;
      setGenerationStatus(hasReviewIssues
        ? (nextPathways.length > 0
            ? 'Recommendations are available, but review issues need adviser attention.'
            : 'The recommender did not return renderable pathway cards. Review issues below show what failed.')
        : 'Recommendations are ready.');
      setGenerationStatusTone(hasReviewIssues ? 'warning' : 'success');
      setCurrentSavedPathwayId(null);
      setIsProfilePanelCollapsed(true);
    } catch (err) {
      setRecommendations([{ title: 'Recommendation unavailable', reason: 'Unable to generate recommendations right now. Please try again.' }]);
      setPathways([]);
      setAlreadyTakenCourses([]);
      setPathwayOpenAIResponseId('');
      setRecommendationReviewIssues([]);
      setRecommendationAdviserNotes([]);
      setRecommendationReviewTrail([]);
      setSelectedPathwayId(null);
      setHighlightedPathwayId(null);
      setSelectedPathwayIds([]);
      setPathwayStage('email');
      setPathwayChatInput('');
      setPathwayChatMessages([]);
      setEmailChatInput('');
      setEmailChatMessages([]);
      setGenerationStatus(err.message || 'Unable to generate recommendations right now.');
      setGenerationStatusTone('error');
    } finally {
      window.clearTimeout(longRunningTimer);
      setLoading(false);
    }
  };

  const hasPathwayOptions = pathways.length > 0;
  const selectedPathways = pathways.filter((pathway) => selectedPathwayIds.includes(pathway.id));
  const selectedPathway = selectedPathways[0] || pathways.find(pathway => pathway.id === selectedPathwayId) || null;
  const displayedRecommendations = selectedPathways.length > 0
    ? selectedPathways.flatMap((pathway) => (pathway.programmes || []).map((programme) => ({
        ...programme,
        pathwayLabel: pathway.label
      })))
    : selectedPathway
      ? selectedPathway.programmes
      : recommendations;
  const hasRecommendationReviewState = recommendationReviewIssues.length > 0
    || recommendationAdviserNotes.length > 0
    || recommendationReviewTrail.length > 0
    || (generationStatus && generationStatusTone === 'error');
  const showRecommendationPanel = (hasPathwayOptions || hasRecommendationReviewState) && ['recommendations', 'email'].includes(pathwayStage);
  const showEmailPanel = displayedRecommendations.length > 0 && (!hasPathwayOptions || pathwayStage === 'email');
  const selectedEmailPathways = selectedPathways.length > 0
    ? selectedPathways
    : selectedPathway
      ? [selectedPathway]
      : displayedRecommendations.length
        ? [{ id: 'recommended-programmes', label: 'Recommended programmes', programmes: displayedRecommendations }]
        : [];
  const selectedPathwayLabelSummary = selectedPathways.length > 0
    ? selectedPathways.map((pathway) => pathway.label).join(', ')
    : 'No pathway selected';
  const selectedTakenCourseTitles = Array.isArray(profile.alreadyTakenCourses) ? profile.alreadyTakenCourses : [];
  const completedCourseRecords = alreadyTakenCourses.length > 0
    ? alreadyTakenCourses
    : selectedTakenCourseTitles.map((title) => ({ title }));
  const selectedCertification = selectedPathway?.certification || (profile.certificationPath || '').toUpperCase();
  const shouldCaptureTakenCourses = ['some-exposure', 'extensive'].includes(profile.priorExecutiveEducation)
    && ['CIM', 'CIF'].includes((profile.certificationPath || '').toUpperCase());
  const eligibleTakenCourseOptions = courses
    .filter((course) => Array.isArray(course.pathways) && course.pathways.includes((profile.certificationPath || '').toUpperCase()))
    .sort((a, b) => a.title.localeCompare(b.title));
  const filteredTakenCourseOptions = eligibleTakenCourseOptions.filter((course) => {
    const notAlreadySelected = !selectedTakenCourseTitles.some((title) => normalizeCourseTitle(title) === normalizeCourseTitle(course.title));
    if (!notAlreadySelected) {
      return false;
    }

    const normalizedQuery = normalizeCourseTitle(takenCourseQuery);
    if (!normalizedQuery) {
      return true;
    }

    return normalizeCourseTitle(course.title).includes(normalizedQuery);
  }).slice(0, 8);
  const objectivePriority = objectiveSuggestionPriority[profile.primaryObjective] || { roles: [], sectors: [] };
  const rankedRoleSuggestions = rankSuggestions(roleSuggestions, objectivePriority.roles);
  const rankedSectorSuggestions = rankSuggestions(sectorSuggestions, objectivePriority.sectors);
  const selectedCareerLevel = selectedCareerLevels[0] || profile.role || '';
  const readinessItems = readinessFields.map((item) => ({
    ...item,
    complete: item.isComplete(profile)
  }));
  const readinessCompleted = readinessItems.filter((item) => item.complete).length;
  const readinessPercent = Math.round((readinessCompleted / readinessItems.length) * 100);
  const profilePanelMeta = profileEntryMode === 'free-text'
    ? (freeTextProfile.trim() ? 'Free text profile' : 'Free text')
    : `${readinessCompleted}/${readinessItems.length} complete`;
  const nextReadinessGap = readinessItems.find((item) => !item.complete);
  const preset = objectivePresets[profile.primaryObjective];
  const canApplyPreset = Boolean(preset);
  const hasPresetGaps = canApplyPreset && Object.entries(preset).some(([field]) => !profile[field]);
  const participantSnapshot = [
    { key: 'profileEntryMode', label: 'Profile Entry Mode', value: profileEntryMode === 'free-text' ? 'Free text' : 'Structured' },
    { key: 'freeTextProfile', label: 'Participant Notes', value: profileEntryMode === 'free-text' ? freeTextProfile : '' },
    { key: 'role', label: 'Career Level', value: selectedCareerLevel },
    { key: 'sector', label: 'Sector', value: profile.sector },
    { key: 'managementExperience', label: 'Management Experience', value: labelValue('managementExperience', profile.managementExperience) },
    { key: 'primaryObjective', label: 'Primary Objective', value: labelValue('primaryObjective', profile.primaryObjective) },
    { key: 'currentChallenge', label: 'Current Challenge', value: labelValue('currentChallenge', profile.currentChallenge) },
    { key: 'desiredOutcome', label: 'Desired Outcome', value: labelValue('desiredOutcome', profile.desiredOutcome) },
    { key: 'preferredEmphasis', label: 'Preferred Emphasis', value: labelValue('preferredEmphasis', profile.preferredEmphasis) },
    { key: 'learningStyle', label: 'Learning Style', value: labelValue('learningStyle', profile.learningStyle) },
    { key: 'priorExecutiveEducation', label: 'Prior Exec-Ed', value: labelValue('priorExecutiveEducation', profile.priorExecutiveEducation) },
    { key: 'alreadyTakenCourses', label: 'Already Taken at LBS', value: selectedTakenCourseTitles.join(', ') },
    { key: 'formatPreference', label: 'Format Preference', value: labelValue('formatPreference', profile.formatPreference) },
    { key: 'completionWindow', label: 'Completion Window', value: labelValue('completionWindow', profile.completionWindow) }
  ].filter((entry) => Boolean((entry.value || '').trim()));
  const generatedDraftEmailText = useMemo(() => {
    if (displayedRecommendations.length === 0) return '';
    return buildDraftEmail(
      selectedPathways.length > 1 ? selectedPathways : selectedPathway,
      displayedRecommendations,
      selectedCertification,
      participantSnapshot,
      profile.goals || freeTextProfile,
      courses
    );
  }, [courses, displayedRecommendations, freeTextProfile, participantSnapshot, profile.goals, selectedCertification, selectedPathway, selectedPathways]);

  const generatedDraftEmailHtml = useMemo(() => {
    if (displayedRecommendations.length === 0) return '';
    return buildDraftEmailHtml(
      selectedPathways.length > 1 ? selectedPathways : selectedPathway,
      displayedRecommendations,
      selectedCertification,
      participantSnapshot,
      profile.goals || freeTextProfile,
      courses
    );
  }, [courses, displayedRecommendations, freeTextProfile, participantSnapshot, profile.goals, selectedCertification, selectedPathway, selectedPathways]);

  useEffect(() => {
    if (preserveLoadedDraftRef.current) {
      previousGeneratedDraftRef.current = generatedDraftEmailText;
      preserveLoadedDraftRef.current = false;
      return;
    }

    setDraftEmailText((currentDraft) => {
      const shouldReplaceDraft = !currentDraft || currentDraft === previousGeneratedDraftRef.current;
      if (shouldReplaceDraft) {
        setDraftEmailHtmlContent(generatedDraftEmailHtml || plainTextEmailToHtml(generatedDraftEmailText));
        return generatedDraftEmailText;
      }
      return currentDraft;
    });
    previousGeneratedDraftRef.current = generatedDraftEmailText;
  }, [generatedDraftEmailHtml, generatedDraftEmailText]);

  useEffect(() => {
    if (!draftEditorRef.current) return;
    const nextHtml = draftEmailHtmlContent || plainTextEmailToHtml(draftEmailText || generatedDraftEmailText);
    if (draftEditorRef.current.innerHTML !== nextHtml) {
      draftEditorRef.current.innerHTML = nextHtml;
    }
  }, [draftEmailHtmlContent, draftEmailText, generatedDraftEmailText]);

  const draftEmailHtml = useMemo(
    () => draftEmailHtmlContent || plainTextEmailToHtml(draftEmailText || generatedDraftEmailText) || generatedDraftEmailHtml,
    [draftEmailHtmlContent, draftEmailText, generatedDraftEmailHtml, generatedDraftEmailText]
  );

  const handleCopyDraftEmail = async () => {
    const emailText = draftEmailText || generatedDraftEmailText;
    const emailHtml = draftEmailHtml;

    try {
      if (navigator.clipboard && navigator.clipboard.write && typeof ClipboardItem !== 'undefined') {
        const clipboardItem = new ClipboardItem({
          'text/plain': new Blob([emailText], { type: 'text/plain' }),
          'text/html': new Blob([emailHtml], { type: 'text/html' })
        });
        await navigator.clipboard.write([clipboardItem]);
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(emailText);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = emailText;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      setCopyStatus('Draft email copied');
    } catch (error) {
      setCopyStatus('Unable to copy draft email');
    }

    if (copyStatusTimeoutRef.current) {
      clearTimeout(copyStatusTimeoutRef.current);
    }

    copyStatusTimeoutRef.current = setTimeout(() => {
      setCopyStatus('');
    }, 2200);
  };

  const reloadSavedPathways = async () => {
    const response = await request('/api/pathways/saved');
    if (!response.ok) return;
    const data = await response.json();
    setSavedPathways(Array.isArray(data.pathways) ? data.pathways : []);
  };

  const getDefaultPathwayTitle = () => {
    const savedPathway = savedPathways.find((pathway) => pathway.id === currentSavedPathwayId);
    return savedPathway?.title
      || selectedPathway?.label
      || labelValue('primaryObjective', profile.primaryObjective)
      || 'Saved pathway';
  };

  const openSavePathwayDialog = () => {
    setSavePathwayTitle(getDefaultPathwayTitle());
    setSavePathwayError('');
    setIsSaveDialogOpen(true);
  };

  const closeSavePathwayDialog = () => {
    if (isSavingPathway) return;
    setIsSaveDialogOpen(false);
    setSavePathwayTitle('');
    setSavePathwayError('');
  };

  const formatDraftEmail = (command) => {
    if (!draftEditorRef.current || isDraftGenerating) return;
    draftEditorRef.current.focus();
    document.execCommand(command, false, null);
    setDraftEmailHtmlContent(draftEditorRef.current.innerHTML);
    setDraftEmailText(draftEditorRef.current.innerText);
    setIsPathwayDirty(true);
  };

  const persistCurrentPathway = async (titleValue) => {
    const title = (titleValue || '').trim();
    if (!title) {
      setSavePathwayError('Enter a pathway name.');
      return;
    }

    setIsSavingPathway(true);
    setSavePathwayError('');

    try {
      const response = await request('/api/pathways/saved', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: currentSavedPathwayId,
          title,
          profile,
          recommendations,
          pathways,
          selectedPathwayId,
          selectedPathwayIds,
          draftEmailText,
          draftEmailHtml,
          chatMessages: pathwayChatMessages,
          emailChatMessages,
          workspaceState: {
            pathwayStage,
            highlightedPathwayId,
            chatModel: pathwayChatModel,
            chatReasoningEffort: pathwayChatReasoningEffort,
            chatInput: pathwayChatInput,
            emailChatModel,
            emailChatReasoningEffort,
            emailChatInput,
            alreadyTakenCourses,
            isProfilePanelCollapsed,
            profileEntryMode,
            freeTextProfile,
            pathwayOpenAIResponseId,
            recommendationWarnings: recommendationReviewIssues,
            recommendationReviewIssues,
            recommendationAdviserNotes,
            recommendationReviewTrail
          }
        })
      });

      if (!response.ok) {
        throw new Error('Unable to save pathway');
      }

      const data = await response.json();
      setCurrentSavedPathwayId(data.pathway?.id || currentSavedPathwayId);
      setIsPathwayDirty(false);
      setIsSaveDialogOpen(false);
      setSavePathwayTitle('');
      await reloadSavedPathways();
    } catch (error) {
      setSavePathwayError('Unable to save pathway.');
      setSavePathwayTitle(title);
      setIsSaveDialogOpen(true);
    } finally {
      setIsSavingPathway(false);
    }
  };

  const saveCurrentPathway = async (event) => {
    event?.preventDefault?.();
    await persistCurrentPathway(savePathwayTitle);
  };

  const handleHeaderSavePathway = async () => {
    if (currentSavedPathwayId) {
      await persistCurrentPathway(getDefaultPathwayTitle());
      return;
    }

    openSavePathwayDialog();
  };

  const startRenamingSavedPathway = (savedPathway) => {
    setEditingPathwayId(savedPathway.id);
    setEditingPathwayTitle(savedPathway.title || '');
  };

  const renameSavedPathway = async (pathwayId) => {
    const title = editingPathwayTitle.trim();
    if (!title) return;

    const savedPathway = savedPathways.find((pathway) => pathway.id === pathwayId);
    if (!savedPathway) return;

    try {
      const response = await request('/api/pathways/saved', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...savedPathway,
          id: pathwayId,
          title
        })
      });

      if (response.ok) {
        await reloadSavedPathways();
      }
    } finally {
      setEditingPathwayId(null);
      setEditingPathwayTitle('');
    }
  };

  const loadSavedPathway = (savedPathway) => {
    if (isPathwayDirty && !window.confirm('Load this saved pathway? Unsaved changes will be lost.')) {
      return;
    }

    setIsSavedPathwaysOpen(false);
    setEditingPathwayId(null);
    setEditingPathwayTitle('');
    setIsSaveDialogOpen(false);
    setCurrentSavedPathwayId(savedPathway.id);
    preserveLoadedDraftRef.current = true;
    const workspaceState = savedPathway.workspaceState || {};
    setProfile({ ...defaultProfile, ...(savedPathway.profile || {}) });
    setProfileEntryMode(workspaceState.profileEntryMode || savedPathway.profileEntryMode || 'structured');
    setFreeTextProfile(workspaceState.freeTextProfile || savedPathway.freeTextProfile || '');
    setRecommendations(savedPathway.recommendations || []);
    setPathways(savedPathway.pathways || []);
    const preferredPathwayId = getPreferredPathwayId(savedPathway.pathways || [], savedPathway.preferredPathwayId);
    const savedSelectedIds = Array.isArray(savedPathway.selectedPathwayIds) && savedPathway.selectedPathwayIds.length
      ? savedPathway.selectedPathwayIds
      : [savedPathway.selectedPathwayId || preferredPathwayId || savedPathway.pathways?.[0]?.id].filter(Boolean);
    setSelectedPathwayIds(savedSelectedIds);
    setSelectedPathwayId(savedSelectedIds[0] || null);
    setHighlightedPathwayId(workspaceState.highlightedPathwayId || savedSelectedIds[0] || null);
    setPathwayStage(workspaceState.pathwayStage || ((savedPathway.pathways?.length || savedPathway.recommendations?.length) ? 'email' : 'profile'));
    setPathwayChatInput(workspaceState.chatInput || '');
    const savedChatMessages = Array.isArray(savedPathway.chatMessages) ? savedPathway.chatMessages : [];
    setPathwayChatMessages(savedChatMessages.length > 0
      ? savedChatMessages
      : (savedPathway.pathways?.length ? [{ role: 'assistant', content: buildInitialPathwayChatMessage(savedPathway.pathways) }] : []));
    if (workspaceState.chatModel) {
      setPathwayChatModel(workspaceState.chatModel);
    }
    if (workspaceState.chatReasoningEffort !== undefined) {
      setPathwayChatReasoningEffort(workspaceState.chatReasoningEffort || '');
    }
    setEmailChatInput(workspaceState.emailChatInput || '');
    const savedEmailChatMessages = Array.isArray(savedPathway.emailChatMessages) ? savedPathway.emailChatMessages : [];
    setEmailChatMessages(savedEmailChatMessages);
    if (workspaceState.emailChatModel) {
      setEmailChatModel(workspaceState.emailChatModel);
    }
    if (workspaceState.emailChatReasoningEffort !== undefined) {
      setEmailChatReasoningEffort(workspaceState.emailChatReasoningEffort || '');
    }
    setDraftEmailText(savedPathway.draftEmailText || '');
    setDraftEmailHtmlContent(savedPathway.draftEmailHtml || plainTextEmailToHtml(savedPathway.draftEmailText || ''));
    setAlreadyTakenCourses(workspaceState.alreadyTakenCourses || savedPathway.alreadyTakenCourses || savedPathway.profile?.alreadyTakenCourses || []);
    setPathwayOpenAIResponseId(workspaceState.pathwayOpenAIResponseId || savedPathway.openaiResponseId || '');
    setRecommendationReviewIssues(normalizeReviewIssues(workspaceState.recommendationReviewIssues || workspaceState.recommendationWarnings || []));
    setRecommendationAdviserNotes(Array.isArray(workspaceState.recommendationAdviserNotes) ? workspaceState.recommendationAdviserNotes : []);
    setRecommendationReviewTrail(Array.isArray(workspaceState.recommendationReviewTrail) ? workspaceState.recommendationReviewTrail : []);
    setGenerationStatus('');
    setGenerationStatusTone('neutral');
    setIsProfilePanelCollapsed(Boolean(workspaceState.isProfilePanelCollapsed));
    setIsPathwayDirty(false);
  };

  const deleteSavedPathwayById = async (pathwayId) => {
    if (!window.confirm('Delete this saved pathway?')) return;

    const response = await request(`/api/pathways/saved/${pathwayId}`, { method: 'DELETE' });
    if (response.ok) {
      if (currentSavedPathwayId === pathwayId) {
        setCurrentSavedPathwayId(null);
        setIsPathwayDirty(false);
      }
      await reloadSavedPathways();
    }
  };

  const addAlreadyTakenCourse = (courseTitle) => {
    if (!courseTitle) return;
    setIsPathwayDirty(true);

    setProfile((prev) => {
      const currentTitles = Array.isArray(prev.alreadyTakenCourses) ? prev.alreadyTakenCourses : [];
      if (currentTitles.some((title) => normalizeCourseTitle(title) === normalizeCourseTitle(courseTitle))) {
        return prev;
      }

      return {
        ...prev,
        alreadyTakenCourses: [...currentTitles, courseTitle]
      };
    });

    setTakenCourseQuery('');
  };

  const removeAlreadyTakenCourse = (courseTitle) => {
    setIsPathwayDirty(true);
    setProfile((prev) => ({
      ...prev,
      alreadyTakenCourses: (Array.isArray(prev.alreadyTakenCourses) ? prev.alreadyTakenCourses : [])
        .filter((title) => normalizeCourseTitle(title) !== normalizeCourseTitle(courseTitle))
    }));
  };

  const buildSelectedProgrammeList = (pathwayOptions) => {
    if (pathwayOptions.length > 0) {
      return pathwayOptions.flatMap((pathway) => (pathway.programmes || []).map((programme) => ({
        ...programme,
        pathwayLabel: pathway.label
      })));
    }

    return displayedRecommendations;
  };

  const generateInitialEmailDraft = async (pathwayOptions) => {
    const selectedProgrammeList = buildSelectedProgrammeList(pathwayOptions);
    const certification = pathwayOptions[0]?.certification || selectedCertification;
    const fallbackText = buildDraftEmail(
      pathwayOptions.length > 1 ? pathwayOptions : pathwayOptions[0] || selectedPathway,
      selectedProgrammeList,
      certification,
      participantSnapshot,
      profile.goals || freeTextProfile,
      courses
    );

    const fallbackHtml = buildDraftEmailHtml(
      pathwayOptions.length > 1 ? pathwayOptions : pathwayOptions[0] || selectedPathway,
      selectedProgrammeList,
      certification,
      participantSnapshot,
      profile.goals || freeTextProfile,
      courses
    );

    const initialDraftMessageId = `email-draft-initial-${Date.now()}`;
    setIsDraftGenerating(true);
    setDraftEmailText('Generating draft...');
    setDraftEmailHtmlContent(plainTextEmailToHtml('Generating draft...'));
    setEmailChatInput('');
    setEmailChatMessages([
      {
        id: initialDraftMessageId,
        role: 'assistant',
        content: 'Creating the first draft from the selected pathway options...',
        isPending: true
      }
    ]);

    try {
      const response = await request('/api/pathways/email-draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profile: { ...profile, profileEntryMode, freeTextProfile },
          selectedPathways: pathwayOptions,
          participantSnapshot,
          fallbackDraft: fallbackText,
          model: emailChatModel,
          reasoningEffort: emailChatReasoningEffort
        })
      });

      if (!response.ok) {
        throw new Error('Email draft request failed');
      }

      const data = await response.json();
      const nextDraftText = data.draftText || fallbackText;
      setDraftEmailText(nextDraftText);
      setDraftEmailHtmlContent(plainTextEmailToHtml(nextDraftText));
      setEmailChatMessages([
        {
          id: `${initialDraftMessageId}-done`,
          role: 'assistant',
          content: 'I drafted the message from the participant profile and selected pathway options. Ask me to make it shorter, warmer, more senior, or clearer about the alternatives.'
        }
      ]);
    } catch (error) {
      setDraftEmailText(fallbackText);
      setDraftEmailHtmlContent(fallbackHtml || plainTextEmailToHtml(fallbackText));
      setEmailChatMessages([
        {
          id: `${initialDraftMessageId}-fallback`,
          role: 'assistant',
          content: 'I used the local pathway draft because the drafting service was unavailable. I can still help refine the text once the service responds again.'
        }
      ]);
    } finally {
      previousGeneratedDraftRef.current = fallbackText;
      setIsDraftGenerating(false);
    }
  };

  const handleSelectPathway = async (pathwayId) => {
    const nextSelectedIds = selectedPathwayIds.length > 0 ? selectedPathwayIds : [pathwayId];
    const nextSelectedPathways = pathways.filter((pathway) => nextSelectedIds.includes(pathway.id));
    setSelectedPathwayId(nextSelectedIds[0] || pathwayId);
    setSelectedPathwayIds(nextSelectedIds);
    setHighlightedPathwayId(nextSelectedIds[0] || pathwayId);
    setPathwayStage('email');
    setIsRecommendationsPanelCollapsed(true);
    setIsEmailPanelCollapsed(false);
    preserveLoadedDraftRef.current = false;
    setIsPathwayDirty(true);
    await generateInitialEmailDraft(nextSelectedPathways);
  };

  const toggleSelectedPathway = (pathwayId) => {
    setIsPathwayDirty(true);
    setSelectedPathwayIds((currentIds) => {
      const nextIds = currentIds.includes(pathwayId)
        ? currentIds.filter((id) => id !== pathwayId)
        : [...currentIds, pathwayId];
      if (nextIds.length > 0) {
        setHighlightedPathwayId(nextIds[0]);
      } else {
        setHighlightedPathwayId(null);
      }
      return nextIds;
    });
  };

  const confirmSelectedPathways = () => {
    if (selectedPathwayIds.length === 0) return;
    handleSelectPathway(selectedPathwayIds[0]);
  };

  const selectPathwayChatModel = (model, effort = '') => {
    setIsPathwayDirty(true);
    setPathwayChatModel(model.id);
    if (model.supportsReasoning) {
      setPathwayChatReasoningEffort(effort || model.reasoningEfforts?.[1] || model.reasoningEfforts?.[0] || '');
    } else {
      setPathwayChatReasoningEffort('');
    }
  };

  const selectEmailChatModel = (model, effort = '') => {
    setIsPathwayDirty(true);
    setEmailChatModel(model.id);
    if (model.supportsReasoning) {
      setEmailChatReasoningEffort(effort || model.reasoningEfforts?.[1] || model.reasoningEfforts?.[0] || '');
    } else {
      setEmailChatReasoningEffort('');
    }
  };

  const handlePathwayChatSubmit = async (event, starterMessage = '') => {
    event?.preventDefault?.();
    const message = (starterMessage || pathwayChatInput).trim();
    if (!message || isPathwayChatLoading) return;

    const nextMessages = [
      ...pathwayChatMessages,
      { role: 'user', content: message }
    ];
    setPathwayChatMessages(nextMessages);
    setPathwayChatInput('');
    setIsPathwayChatLoading(true);
    setIsPathwayDirty(true);

    try {
      const response = await request('/api/pathways/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profile: { ...profile, profileEntryMode, freeTextProfile },
          pathways,
          selectedPathwayId: highlightedPathwayId,
          selectedPathwayIds,
          openaiResponseId: pathwayOpenAIResponseId,
          model: pathwayChatModel,
          reasoningEffort: pathwayChatReasoningEffort,
          message,
          history: pathwayChatMessages
        })
      });

      if (!response.ok) {
        throw new Error('Pathway chat request failed');
      }

      const data = await response.json();
      if (data.openaiResponseId) {
        setPathwayOpenAIResponseId(data.openaiResponseId);
      }
      if (Array.isArray(data.pathways) && data.pathways.length > 0) {
        const preferredPathwayId = getPreferredPathwayId(data.pathways, data.preferredPathwayId);
        setPathways(data.pathways);
        setRecommendations(Array.isArray(data.recommendations) ? data.recommendations : data.pathways[0]?.programmes || []);
        setSelectedPathwayIds(preferredPathwayId ? [preferredPathwayId] : []);
        setSelectedPathwayId(preferredPathwayId || null);
        setHighlightedPathwayId(preferredPathwayId || null);
        setRecommendationReviewIssues([]);
        setRecommendationAdviserNotes(normalizeAdviserNotesFromResult(data));
        setRecommendationReviewTrail([]);
      }
      if (data.suggestedPathwayId) {
        setHighlightedPathwayId(data.suggestedPathwayId);
      }
      setPathwayChatMessages([
        ...nextMessages,
        { role: 'assistant', content: data.response || 'I could not produce a pathway response. Please try again.' }
      ]);
    } catch (error) {
      const fallback = buildLocalPathwayChatResponse(pathways, message);
      if (fallback.suggestedPathwayId) {
        setHighlightedPathwayId(fallback.suggestedPathwayId);
      }
      setPathwayChatMessages([
        ...nextMessages,
        { role: 'assistant', content: fallback.response }
      ]);
    } finally {
      setIsPathwayChatLoading(false);
    }
  };

  const handleEmailChatSubmit = async (event, starterMessage = '') => {
    event?.preventDefault?.();
    const message = (starterMessage || emailChatInput).trim();
    if (!message) return;
    const turnId = `email-draft-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const userMessage = { id: `${turnId}-user`, role: 'user', content: message };
    const pendingMessage = {
      id: `${turnId}-assistant`,
      role: 'assistant',
      content: 'Working on that...',
      isPending: true
    };
    const editorText = draftEditorRef.current?.innerText || '';
    const editorHtml = draftEditorRef.current?.innerHTML || '';
    const currentDraft = editorText.trim() ? editorText : draftEmailText || generatedDraftEmailText;
    const draftIsStillPreparing = isDraftGenerating || currentDraft.trim().toLowerCase() === 'generating draft...';
    if (editorHtml && editorText !== draftEmailText) {
      setDraftEmailText(editorText);
      setDraftEmailHtmlContent(editorHtml);
    }

    if (draftIsStillPreparing) {
      setEmailChatMessages((currentMessages) => [
        ...currentMessages,
        userMessage,
        {
          id: `${turnId}-assistant`,
          role: 'assistant',
          content: 'I am still preparing the first draft. I captured your request here; once the draft appears, send it again and I will revise the message.'
        }
      ]);
      setEmailChatInput('');
      setIsPathwayDirty(true);
      return;
    }

    if (isEmailChatLoading) {
      setEmailChatMessages((currentMessages) => [
        ...currentMessages,
        userMessage,
        {
          id: `${turnId}-assistant`,
          role: 'assistant',
          content: 'I am still working on the previous drafting request. I captured this message here so the conversation does not disappear.'
        }
      ]);
      setEmailChatInput('');
      setIsPathwayDirty(true);
      return;
    }

    setEmailChatMessages((currentMessages) => [
      ...currentMessages,
      userMessage,
      pendingMessage
    ]);
    setEmailChatInput('');
    setIsEmailChatLoading(true);
    setIsPathwayDirty(true);

    try {
      const response = await request('/api/pathways/email-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profile: { ...profile, profileEntryMode, freeTextProfile },
          selectedPathways: selectedEmailPathways,
          participantSnapshot,
          currentDraft,
          model: emailChatModel,
          reasoningEffort: emailChatReasoningEffort,
          message,
          history: emailChatMessages
        })
      });

      if (!response.ok) {
        throw new Error('Email chat request failed');
      }

      const data = await response.json();
      let assistantMessage = data.reply || 'I updated the draft.';
      if (data.draftText) {
        setDraftEmailText(data.draftText);
        setDraftEmailHtmlContent(plainTextEmailToHtml(data.draftText));
      }
      setEmailChatMessages((currentMessages) => currentMessages.map((chatMessage) => (
        chatMessage.id === pendingMessage.id
          ? {
              id: pendingMessage.id,
              role: 'assistant',
              content: assistantMessage,
              draftText: data.draftText || ''
            }
          : chatMessage
      )));
    } catch (error) {
      setEmailChatMessages((currentMessages) => currentMessages.map((chatMessage) => (
        chatMessage.id === pendingMessage.id
          ? {
              id: pendingMessage.id,
              role: 'assistant',
              content: 'I could not reach the drafting assistant. Please try again in a moment.'
            }
          : chatMessage
      )));
    } finally {
      setIsEmailChatLoading(false);
    }
  };

  const handleDraftEmailInput = (event) => {
    setDraftEmailText(event.currentTarget.innerText);
    setDraftEmailHtmlContent(event.currentTarget.innerHTML);
    setIsPathwayDirty(true);
  };

  const startNewPathway = () => {
    if (isPathwayDirty && !window.confirm('Start a new pathway? Unsaved changes will be lost.')) {
      return;
    }

    setProfile(defaultProfile);
    setProfileEntryMode('structured');
    setFreeTextProfile('');
    setRecommendations([]);
    setPathways([]);
    setAlreadyTakenCourses([]);
    setPathwayOpenAIResponseId('');
    setGenerationStatus('');
    setGenerationStatusTone('neutral');
    setRecommendationReviewIssues([]);
    setRecommendationAdviserNotes([]);
    setRecommendationReviewTrail([]);
    setSelectedPathwayId(null);
    setHighlightedPathwayId(null);
    setSelectedPathwayIds([]);
    setCurrentSavedPathwayId(null);
    setEditingPathwayId(null);
    setEditingPathwayTitle('');
    setIsSaveDialogOpen(false);
    setDraftEmailText('');
    setDraftEmailHtmlContent('');
    setTakenCourseQuery('');
    setIsProfilePanelCollapsed(false);
    setIsPathwayDirty(false);
    setIsSavedPathwaysOpen(false);
    setPathwayStage('profile');
    setPathwayChatInput('');
    setPathwayChatMessages([]);
    setEmailChatInput('');
    setEmailChatMessages([]);
    window.localStorage.removeItem(PATHWAY_DRAFT_KEY);
  };

  const closeSavedPathways = () => {
    setIsSavedPathwaysOpen(false);
    setEditingPathwayId(null);
    setEditingPathwayTitle('');
  };

  const cancelRenamingSavedPathway = () => {
    setEditingPathwayId(null);
    setEditingPathwayTitle('');
  };

  return (
    <section className="page page-pathways">
      <PathwayHistoryDrawer
        isOpen={isSavedPathwaysOpen}
        savedPathways={savedPathways}
        currentSavedPathwayId={currentSavedPathwayId}
        editingPathwayId={editingPathwayId}
        editingPathwayTitle={editingPathwayTitle}
        onClose={closeSavedPathways}
        onLoadPathway={loadSavedPathway}
        onStartRenaming={startRenamingSavedPathway}
        onEditingTitleChange={setEditingPathwayTitle}
        onCancelRenaming={cancelRenamingSavedPathway}
        onRenamePathway={renameSavedPathway}
        onDeletePathway={deleteSavedPathwayById}
      />

      <div className="pathway-stack schema-scroll">
        <PathwayPanel
          title="Participant profile"
          icon={ClipboardCheck}
          meta={profilePanelMeta}
          open={!isProfilePanelCollapsed}
          onToggle={() => setIsProfilePanelCollapsed((previous) => !previous)}
          actions={(
            <div className="pathway-panel-tools">
              <button
                type="button"
                className="icon-btn pathway-header-icon-btn"
                onClick={startNewPathway}
                aria-label="New pathway"
                title="New pathway"
              >
                <Plus size={17} />
              </button>
              <button
                type="button"
                className="icon-btn pathway-header-icon-btn"
                onClick={handleHeaderSavePathway}
                aria-label="Save pathway"
                title="Save pathway"
                disabled={isSavingPathway}
              >
                <Save size={17} />
              </button>
              <button
                type="button"
                className="icon-btn pathway-header-icon-btn pathway-history-btn"
                onClick={() => setIsSavedPathwaysOpen(true)}
                aria-label="Open saved pathways"
                title="Saved pathways"
              >
                <History size={17} />
              </button>
            </div>
          )}
        >
          <div className="pathway-form">
            <CertificationPathways />
            <div className="pathway-profile-mode-tabs" role="tablist" aria-label="Participant profile entry mode">
              <button
                type="button"
                className={profileEntryMode === 'structured' ? 'active' : ''}
                onClick={() => handleProfileEntryModeChange('structured')}
              >
                Structured
              </button>
              <button
                type="button"
                className={profileEntryMode === 'free-text' ? 'active' : ''}
                onClick={() => handleProfileEntryModeChange('free-text')}
              >
                Free Text
              </button>
            </div>

            {profileEntryMode === 'structured' ? (
              <>
            <div className="profile-readiness-card">
              <div className="profile-readiness-header">
                <div>
                  <div className="profile-readiness-title">Profile readiness</div>
                  <p className="profile-readiness-copy">{readinessPercent}% complete</p>
                </div>
                <span className={`tag ${readinessCompleted === readinessItems.length ? 'keyword' : 'audience'}`}>
                  {readinessCompleted}/{readinessItems.length} complete
                </span>
              </div>
              <div className="profile-readiness-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={readinessPercent}>
                <div className="profile-readiness-progress-fill" style={{ width: `${readinessPercent}%` }} />
              </div>
              <div className="profile-readiness-inline-actions">
                <p className="profile-readiness-copy">
                  {nextReadinessGap
                    ? `Next: ${nextReadinessGap.hint}`
                    : 'Profile is complete enough to compare pathways confidently.'}
                </p>
                <button
                  type="button"
                  className="profile-readiness-toggle"
                  onClick={() => setShowReadinessDetails((previous) => !previous)}
                >
                  {showReadinessDetails ? 'Hide checklist' : 'Show checklist'}
                </button>
              </div>
              {showReadinessDetails && (
                <div className="profile-readiness-grid">
                  {readinessItems.map((item) => (
                    <div key={item.key} className={`profile-readiness-item ${item.complete ? 'complete' : 'pending'}`}>
                      <span className="profile-readiness-icon" aria-hidden="true">{item.complete ? '✓' : '○'}</span>
                      <span>{item.label}</span>
                    </div>
                  ))}
                </div>
              )}
              {canApplyPreset && hasPresetGaps && (
                <div className="pathway-preset-row">
                  <p className="pathway-preset-copy">
                    Apply suggested defaults for this objective to prefill pathway emphasis, timing, delivery format, and travel assumptions.
                  </p>
                  <button type="button" className="btn-primary" onClick={applyObjectivePreset}>
                    Apply Suggested Defaults
                  </button>
                </div>
              )}
            </div>

            <div className="pathway-grid">
              <div className="field-with-hint">
                <CreatableDropdown
                  label="Career Level"
                  tooltip="Select a career level, or type a custom level."
                  value={selectedCareerLevel}
                  options={rankedRoleSuggestions}
                  onChange={handleCareerLevelChange}
                  placeholder="Select or type career level"
                />
              </div>

              <div className="field-with-hint">
                <CreatableDropdown
                  label="Sector"
                  tooltip="Include industry context to improve example and pathway relevance. Suggestions are prioritised by primary objective."
                  value={profile.sector}
                  options={rankedSectorSuggestions}
                  onChange={(value) => handleInputChange('sector', value)}
                  placeholder="Select or type sector"
                />
              </div>

              <div className="field-with-hint">
                <CustomDropdown
                  label="Management Experience"
                  tooltip="Use total people-management experience, not just tenure in current role."
                  value={profile.managementExperience}
                  onChange={(value) => handleInputChange('managementExperience', value)}
                  options={[
                    { value: '0-3', label: '0-3 years' },
                    { value: '4-7', label: '4-7 years' },
                    { value: '8-12', label: '8-12 years' },
                    { value: '12+', label: '12+ years' }
                  ]}
                  placeholder="Select management experience"
                />
              </div>

              <div className="field-with-hint">
                <CustomDropdown
                  label="Primary Objective"
                  tooltip="Pick the single most important outcome for this intake cycle."
                  value={profile.primaryObjective}
                  onChange={(value) => handleInputChange('primaryObjective', value)}
                  options={[
                    { value: 'broader-leadership', label: 'Broader general management capability' },
                    { value: 'lead-change', label: 'Lead change and organisational transformation' },
                    { value: 'strategic-growth', label: 'Sharpen strategy and growth decisions' },
                    { value: 'finance-fluency', label: 'Build finance confidence' },
                    { value: 'digital-transformation', label: 'Lead digital transformation' }
                  ]}
                  placeholder="Select primary objective"
                />
              </div>

              <div className="field-with-hint">
                <CustomDropdown
                  label="Current Challenge"
                  tooltip="What is the most immediate blocker this participant needs to address?"
                  value={profile.currentChallenge}
                  onChange={(value) => handleInputChange('currentChallenge', value)}
                  options={[
                    { value: 'cross-functional-alignment', label: 'Cross-functional alignment and execution drift' },
                    { value: 'decision-velocity', label: 'Slow strategic decision velocity' },
                    { value: 'change-adoption', label: 'Low change adoption and engagement' },
                    { value: 'commercial-acumen', label: 'Commercial and financial confidence gap' },
                    { value: 'digital-prioritization', label: 'Digital prioritization and ROI uncertainty' },
                    { value: 'other', label: 'Other challenge' }
                  ]}
                  placeholder="Select immediate challenge"
                />
              </div>

              <div className="field-with-hint">
                <CustomDropdown
                  label="Desired Role Outcome"
                  tooltip="Capture the role or capability shift this pathway should support."
                  value={profile.desiredOutcome}
                  onChange={(value) => handleInputChange('desiredOutcome', value)}
                  options={[
                    { value: 'step-into-gm', label: 'Prepare to step into General Management' },
                    { value: 'lead-transformation-programmes', label: 'Lead enterprise transformation programmes' },
                    { value: 'improve-strategic-finance', label: 'Strengthen strategic finance and board fluency' },
                    { value: 'scale-digital-execution', label: 'Scale digital execution across functions' },
                    { value: 'build-leadership-bench', label: 'Build leadership bench strength for the team' }
                  ]}
                  placeholder="Select desired outcome"
                />
              </div>

              <div className="field-with-hint">
                <CustomDropdown
                  label="Preferred Emphasis"
                  tooltip="Optional override if the participant wants a stronger thematic tilt."
                  value={profile.preferredEmphasis}
                  onChange={(value) => handleInputChange('preferredEmphasis', value)}
                  options={[
                    { value: 'balanced', label: 'Balanced pathway' },
                    { value: 'leadership', label: 'Leadership-heavy' },
                    { value: 'strategy', label: 'Strategy-heavy' },
                    { value: 'finance', label: 'Finance-heavy' },
                    { value: 'digital', label: 'Digital/transformation-heavy' }
                  ]}
                  placeholder="Select pathway emphasis"
                />
              </div>

              <div className="field-with-hint">
                <CustomDropdown
                  label="Learning Style"
                  tooltip="Choose the format of learning experience the participant responds to best."
                  value={profile.learningStyle}
                  onChange={(value) => handleInputChange('learningStyle', value)}
                  options={[
                    { value: 'case-driven', label: 'Case-driven and discussion-heavy' },
                    { value: 'toolkit-first', label: 'Practical toolkit and frameworks first' },
                    { value: 'coaching-reflective', label: 'Coaching and reflective learning' },
                    { value: 'cohort', label: 'Peer cohort learning' }
                  ]}
                  placeholder="Select learning style"
                />
              </div>

              <div className="field-with-hint">
                <CustomDropdown
                  label="Prior Executive Education"
                  tooltip="Use prior exposure to calibrate programme stretch and novelty."
                  value={profile.priorExecutiveEducation}
                  onChange={(value) => handleInputChange('priorExecutiveEducation', value)}
                  options={[
                    { value: 'none', label: 'No prior executive education' },
                    { value: 'some-exposure', label: 'Some prior executive education' },
                    { value: 'extensive', label: 'Extensive prior executive education' }
                  ]}
                  placeholder="Select prior exposure"
                />
              </div>

              <div className="field-with-hint">
                <CustomDropdown
                  label="Format Preference"
                  tooltip="Set the most practical delivery mode for attendance and engagement."
                  value={profile.formatPreference}
                  onChange={(value) => handleInputChange('formatPreference', value)}
                  options={[
                    { value: 'in-person', label: 'Mostly in-person' },
                    { value: 'blended', label: 'Blended learning' },
                    { value: 'online', label: 'Mostly online' },
                    { value: 'no-preference', label: 'No strong preference' }
                  ]}
                  placeholder="Select preferred format"
                />
              </div>

              <div className="field-with-hint">
                <CustomDropdown
                  label="Completion Window"
                  tooltip="Use expected timeline to favour shorter or paced programme pathways."
                  value={profile.completionWindow}
                  onChange={(value) => handleInputChange('completionWindow', value)}
                  options={[
                    { value: '6-months', label: 'Within 6 months' },
                    { value: '12-months', label: 'Within 12 months' },
                    { value: '24-months', label: 'Within full CIM/CIF window' },
                    { value: 'flexible', label: 'Flexible timing' }
                  ]}
                  placeholder="Select timing"
                />
              </div>

              <div className="field-with-hint">
                <CustomDropdown
                  label="Certification Path"
                  tooltip="Choose either CIM (Certificate in Management) or CIF (Certificate in Finance)."
                  value={profile.certificationPath}
                  onChange={(value) => handleInputChange('certificationPath', value)}
                  options={[
                    { value: '', label: 'No certificate' },
                    { value: 'CIM', label: 'CIM (Certificate in Management)' },
                    { value: 'CIF', label: 'CIF (Certificate in Finance)' }
                  ]}
                  placeholder="No certificate"
                />
              </div>

              <div className="pathway-field-full field-with-hint">
                <label className="input-label">Learning Goals and Context <InfoTooltip text="Summarise business context, capability gaps, and key constraints in 1-2 sentences." /></label>
                <textarea
                  value={profile.goals}
                  onChange={(e) => handleInputChange('goals', e.target.value)}
                  placeholder="e.g. Newly promoted operations leader who needs stronger strategic finance, clearer enterprise thinking, and a realistic CiM path around quarterly travel constraints."
                  rows={4}
                />
              </div>

              {shouldCaptureTakenCourses && (
                <div className="pathway-field-wide field-with-hint">
                  <label className="input-label">
                    Already Taken at LBS
                    <InfoTooltip text="Capture completed LBS Executive Education programmes so the pathway can treat them as already completed rather than recommending them again." />
                  </label>
                  {selectedTakenCourseTitles.length > 0 && (
                    <div className="career-selected-row" role="group" aria-label="Already taken programmes">
                      {selectedTakenCourseTitles.map((courseTitle) => (
                        <button
                          key={`taken-${courseTitle}`}
                          type="button"
                          className="quick-pick-chip active"
                          onClick={() => removeAlreadyTakenCourse(courseTitle)}
                        >
                          {courseTitle}
                        </button>
                      ))}
                    </div>
                  )}
                  <input
                    value={takenCourseQuery}
                    onChange={(e) => setTakenCourseQuery(e.target.value)}
                    placeholder={`Search ${profile.certificationPath} programmes already completed`}
                  />
                  {filteredTakenCourseOptions.length > 0 && (
                    <div className="quick-picks" role="group" aria-label="Available already taken course options">
                      {filteredTakenCourseOptions.map((course) => (
                        <button
                          key={`option-${course.id || course.title}`}
                          type="button"
                          className="quick-pick-chip"
                          onClick={() => addAlreadyTakenCourse(course.title)}
                        >
                          {course.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
              </>
            ) : (
              <div className="pathway-free-text-profile">
                <div className="pathway-grid pathway-grid--free-text">
                  <div className="field-with-hint">
                    <CustomDropdown
                      label="Certification Path"
                      tooltip="Choose a certificate pathway, or leave as no certificate for a general recommendation."
                      value={profile.certificationPath}
                      onChange={(value) => handleInputChange('certificationPath', value)}
                      options={[
                        { value: '', label: 'No certificate' },
                        { value: 'CIM', label: 'CIM (Certificate in Management)' },
                        { value: 'CIF', label: 'CIF (Certificate in Finance)' }
                      ]}
                      placeholder="No certificate"
                    />
                  </div>
                  <div className="pathway-field-full field-with-hint">
                    <label className="input-label">Participant Profile <InfoTooltip text="Describe the participant, goals, constraints, prior learning, and any sales positioning context in natural language." /></label>
                    <textarea
                      value={freeTextProfile}
                      onChange={(e) => handleFreeTextProfileChange(e.target.value)}
                      placeholder="e.g. Senior manager in a technology business moving into broader general leadership. Strong operator, needs more enterprise strategy, finance confidence, and a route that can fit around quarterly travel."
                      rows={8}
                    />
                  </div>
                </div>
              </div>
            )}

            {loading && !showRecommendationPanel && (
              <PathwayReviewSummary
                status={generationStatus}
                tone={generationStatusTone}
                isLoading={loading}
              />
            )}

            <div className="pathway-actions">
              <button className="btn-red" onClick={handleRecommend} disabled={loading}>
                {loading ? 'Generating...' : 'Generate Recommendations'}
              </button>
            </div>
          </div>
        </PathwayPanel>

      {showRecommendationPanel && (
        <PathwayPanel
          title="Recommended pathways"
          icon={ListChecks}
          meta={pathwayStage === 'email' ? `Selected: ${selectedPathwayLabelSummary}` : `${selectedPathwayIds.length} selected - ${pathways.length} options`}
          open={!isRecommendationsPanelCollapsed}
          onToggle={() => setIsRecommendationsPanelCollapsed((previous) => !previous)}
          actions={!isRecommendationsPanelCollapsed ? (
            <button type="button" className="btn-primary pathway-primary-action" onClick={confirmSelectedPathways} disabled={selectedPathwayIds.length === 0}>
              <Mail size={16} />
              Confirm and Draft Email
            </button>
          ) : null}
          className="exec-panel--recommendations"
        >
          <PathwayReviewSummary
            status={generationStatus || (recommendationReviewIssues.length || recommendationAdviserNotes.length || recommendationReviewTrail.length ? 'Review notes from the recommendation workflow.' : '')}
            tone={generationStatusTone}
            reviewIssues={recommendationReviewIssues}
            adviserNotes={recommendationAdviserNotes}
            reviewTrail={recommendationReviewTrail}
            isLoading={loading}
          />
          <PathwayRecommendationPanel
            pathways={pathways}
            selectedPathwayIds={selectedPathwayIds}
            completedCourseRecords={completedCourseRecords}
            chatMessages={pathwayChatMessages}
            chatInput={pathwayChatInput}
            isChatLoading={isPathwayChatLoading}
            models={pathwayChatModels}
            selectedModel={pathwayChatModel}
            reasoningEffort={pathwayChatReasoningEffort}
            reviewIssues={recommendationReviewIssues}
            onSelectModel={selectPathwayChatModel}
            onChatInputChange={setPathwayChatInput}
            onChatSubmit={handlePathwayChatSubmit}
            onChatStarter={(starter) => handlePathwayChatSubmit(null, starter)}
            onTogglePathway={toggleSelectedPathway}
          />
        </PathwayPanel>
      )}

      {showEmailPanel && (
        <PathwayPanel
          title="Draft email"
          icon={Mail}
          meta={`${selectedEmailPathways.length} selected`}
          open={!isEmailPanelCollapsed}
          onToggle={() => setIsEmailPanelCollapsed((previous) => !previous)}
          actions={!isEmailPanelCollapsed ? (
            <>
              <button type="button" className="btn-primary pathway-primary-action" onClick={handleCopyDraftEmail} disabled={isDraftGenerating}>
                <Mail size={16} />
                Copy Draft
              </button>
              {copyStatus && <span className="participant-copy-status">{copyStatus}</span>}
            </>
          ) : null}
          className="exec-panel--email"
        >
          <PathwayEmailPanel
            draftEditorRef={draftEditorRef}
            isDraftGenerating={isDraftGenerating}
            chatMessages={emailChatMessages}
            chatInput={emailChatInput}
            isChatLoading={isEmailChatLoading}
            models={pathwayChatModels}
            selectedModel={emailChatModel}
            reasoningEffort={emailChatReasoningEffort}
            onSelectModel={selectEmailChatModel}
            onChatInputChange={setEmailChatInput}
            onChatSubmit={handleEmailChatSubmit}
            onChatStarter={(starter) => handleEmailChatSubmit(null, starter)}
            onDraftEmailInput={handleDraftEmailInput}
            onFormatDraft={formatDraftEmail}
          />
        </PathwayPanel>
      )}
      </div>
      <PathwaySaveDialog
        isOpen={isSaveDialogOpen}
        title={savePathwayTitle}
        error={savePathwayError}
        isSaving={isSavingPathway}
        onTitleChange={setSavePathwayTitle}
        onClose={closeSavePathwayDialog}
        onSave={saveCurrentPathway}
      />
    </section>
  );
};
export default PathwayBuilder;
